/** Shared OTK document contracts derived from the EDRTZ OpenAPI specification. */
import {z} from "zod";
import {ObjectId} from "bson";
import {mongoObjectIdSchema} from "./mongo.ts";

/** Accept Mongo values or JSON values; decoding always returns the wire representation. */
export const otkObjectIdCodec = z.codec(
    z.union([
        z.custom<ObjectId>((value) =>
            typeof value === "object" && value !== null
            && "toHexString" in value && typeof value.toHexString === "function"
            && ObjectId.isValid(value as ObjectId)),
        mongoObjectIdSchema,
    ]),
    mongoObjectIdSchema,
    {decode: (id) => typeof id === "string" ? id : id.toHexString(), encode: (hex) => new ObjectId(hex)},
);

export const otkTimestampCodec = z.codec(
    z.union([z.date(), z.iso.datetime()]),
    z.iso.datetime(),
    {decode: (value) => value instanceof Date ? value.toISOString() : value, encode: (iso) => new Date(iso)},
);

/** Public retry state; other stored submission fields stay on the server. */
const otkDocumentSubmissionSchema = z.object({
    nextRetryAt: otkTimestampCodec,
});

export const DocumentTypes = ["PROTOCOL", "NONCONFORMITY_ACT"] as const;
export const documentTypeSchema = z.enum(DocumentTypes);
export type DocumentType = z.infer<typeof documentTypeSchema>;
export const otkDocumentResultSchema = z.enum(["POSITIVE", "NEGATIVE"]);
export type OtkDocumentResult = z.infer<typeof otkDocumentResultSchema>;

/** `DRAFT` and `SUBMISSION_PENDING` are local states; the remaining states come from EDRTZ. */
export const DocumentStatuses = [
    "DRAFT",
    "SUBMISSION_PENDING",
    "NOT_SENT",
    "NOT_ACCEPTED",
    "SENT",
    "DELIVERED",
    "VERIFIED",
    "INVALID",
    "ANNULLED",
] as const;

export type DocumentStatus = typeof DocumentStatuses[number];
export const documentStatusSchema = z.enum(DocumentStatuses);

export const otkDocumentCountStatuses = [
    "DRAFT", "SUBMISSION_PENDING", "SENT", "VERIFIED", "INVALID", "ANNULLED",
] as const;

/** Backend status groups represented by the document list tabs. */
export const otkDocumentCountStatusGroups = {
    DRAFT: ["DRAFT"],
    SUBMISSION_PENDING: ["SUBMISSION_PENDING"],
    SENT: ["SENT", "DELIVERED"],
    VERIFIED: ["VERIFIED"],
    INVALID: ["INVALID"],
    ANNULLED: ["ANNULLED"],
} as const satisfies Record<(typeof otkDocumentCountStatuses)[number], readonly DocumentStatus[]>;

export const otkDocumentCountsSchema = z.record(
    z.enum(otkDocumentCountStatuses),
    z.union([z.number().int().min(0).max(99), z.literal("99+")]),
);
export type OtkDocumentCounts = z.infer<typeof otkDocumentCountsSchema>;

export const SortSchema = z.enum(['DESC', 'ASC']);
export type Sort = z.infer<typeof SortSchema>;

export const otkOwnerTypeSchema = z.enum(["INDIVIDUAL", "LEGAL_ENTITY"]);
export type OtkOwnerType = z.infer<typeof otkOwnerTypeSchema>;
export const otkEmissionTestTypeSchema = z.enum(["GASOLINE_GAS", "DIESEL_GAS_DIESEL", "NO_EMISSIONS"]);
export type OtkEmissionTestType = z.infer<typeof otkEmissionTestTypeSchema>;
export const otkNonconformityFormSchema = z.enum(["STANDARD", "NONSTANDARD"]);
export const otkDefectSeveritySchema = z.enum(["MINOR", "MAJOR", "DANGEROUS"]);
export type OtkDefectSeverity = z.infer<typeof otkDefectSeveritySchema>;

export const photoCategories = [
    "DASHBOARD",
    "RIGHT_SIDE",
    "LEFT_SIDE",
    "FRONT",
    "REAR",
    "INTERIOR_M2_M3",
    "PEDALS",
    "MIRROR",
    "TACHOGRAPH",
    "TACHOGRAPH_PLATE",
    "ADDITIONAL"
] as const;

// DE7's `photoCategories` — 11 entries across 3 groups, real official labels
// from fotomaterial.md. `visible` is this page's own conditional-rendering
// predicate (isM2M3 / hasTachograph), matching the Conditional-rendering
// rules table; `ADDITIONAL` accepts more than one photo (see DE7).
export type PhotoCategoryId = typeof photoCategories[number];

export const otkDocumentDraftPatchOperationSchema = z.discriminatedUnion("op", [
    z.object({op: z.literal("set"), path: z.string().min(1).max(100), value: z.unknown()}),
    z.object({op: z.literal("unset"), path: z.string().min(1).max(100)}),
]);
export type OtkDocumentDraftPatchOperation = z.infer<typeof otkDocumentDraftPatchOperationSchema>;

export const otkDocumentDraftPatchSchema = z.object({
    expectedVersion: z.number().int().nonnegative(),
    operations: z.array(otkDocumentDraftPatchOperationSchema).min(1).max(50),
});
export type OtkDocumentDraftPatch = z.infer<typeof otkDocumentDraftPatchSchema>;

export const otkNonconformityItemInputSchema = z.discriminatedUnion("form", [
    z.object({form: z.literal("STANDARD"), nonconformityCodeId: z.string().trim().min(1)}),
    z.object({
        form: z.literal("NONSTANDARD"),
        defectType: otkDefectSeveritySchema,
        description: z.string().trim().min(1).max(2000),
    }),
]);
export type OtkNonconformityItemInput = z.infer<typeof otkNonconformityItemInputSchema>;

export const saveNonconformitiesInputSchema = z.object({
    expectedVersion: z.number().int().nonnegative(),
    hasNonconformities: z.boolean(),
    items: z.array(otkNonconformityItemInputSchema).max(840),
}).superRefine(({hasNonconformities, items}, ctx) => {
    if (hasNonconformities && items.length === 0) {
        ctx.addIssue({code: "custom", path: ["items"], message: "At least one nonconformity is required"});
    } else if (!hasNonconformities && items.length > 0) {
        ctx.addIssue({code: "custom", path: ["items"], message: "Items must be empty"});
    }
    const standardCodes = items
        .filter((item) => item.form === "STANDARD")
        .map(({nonconformityCodeId}) => nonconformityCodeId);
    if (new Set(standardCodes).size !== standardCodes.length) {
        ctx.addIssue({code: "custom", path: ["items"], message: "Duplicate standard codes are not allowed"});
    }
});
export type SaveNonconformitiesInput = z.infer<typeof saveNonconformitiesInputSchema>;

export const otkReconstructionItemInputSchema = z.object({
    approved: z.boolean(),
    description: z.string().trim().min(1).max(2000),
    documentName: z.string().trim().min(1).max(500).optional(),
    documentNumber: z.string().trim().min(1).max(200).optional(),
    documentDate: z.iso.date().optional(),
}).superRefine((item, ctx) => {
    if (!item.approved) return;
    for (const field of ["documentName", "documentNumber", "documentDate"] as const) {
        if (!item[field]) ctx.addIssue({code: "custom", path: [field], message: "Required when approved"});
    }
});
export type OtkReconstructionItemInput = z.infer<typeof otkReconstructionItemInputSchema>;

export const saveReconstructionsInputSchema = z.object({
    expectedVersion: z.number().int().nonnegative(),
    hasReconstructions: z.boolean(),
    items: z.array(otkReconstructionItemInputSchema).max(100),
}).superRefine(({hasReconstructions, items}, ctx) => {
    if (hasReconstructions && items.length === 0) {
        ctx.addIssue({code: "custom", path: ["items"], message: "At least one reconstruction is required"});
    } else if (!hasReconstructions && items.length > 0) {
        ctx.addIssue({code: "custom", path: ["items"], message: "Items must be empty"});
    }
});
export type SaveReconstructionsInput = z.infer<typeof saveReconstructionsInputSchema>;

export function makeFilePath(subjectId: string, documentId: string, category: string, fileName: string) {
    return `${subjectId}/${documentId}/${category}/${fileName}`;
}

const nullableOptionalTextSchema = z.string().nullable().optional();
const nullableOptionalIdentifierSchema = z.string().trim().min(1).nullable().optional();
const nullableOptionalNumberSchema = z.number().nullable().optional();

export const dangerousGoodsVehiclePurpose =
    "ТЗ, що використовується для перевезення небезпечних вантажів";

export const defaultOtkDraftValues = {
    vehicle: {category: "M1"},
    owner: {type: "INDIVIDUAL"},
    receiverIsOwner: true,
    testResults: {
        engineType: "NO_EMISSIONS",
        windshieldMarked: true,
        leftSideMarked: true,
        rightSideMarked: true,
        rearWindowMarked: true,
    },
} as const;

export const otkVehicleSnapshotSchema = z.object({
    plateNumber: z.string(),
    registrationCertificate: nullableOptionalTextSchema,
    vin: nullableOptionalTextSchema,
    bodyNumber: nullableOptionalTextSchema,
    chassisNumber: nullableOptionalTextSchema,
    brand: z.string(),
    model: z.string(),
    category: z.string(),
    type: nullableOptionalTextSchema,
    manufactureYear: z.number(),
    registrationDate: z.iso.date().nullable().optional(),
    emissionLevel: nullableOptionalTextSchema,
    currentOdometer: z.number(),
    previousOdometer: nullableOptionalNumberSchema,
    purposes: z.array(z.string()).nullable().optional(),
});

export const otkOwnerSnapshotSchema = z.object({
    type: otkOwnerTypeSchema,
    fullName: nullableOptionalTextSchema,
    taxId: nullableOptionalTextSchema,
    organizationName: nullableOptionalTextSchema,
    edrpou: nullableOptionalTextSchema,
    postalAddress: nullableOptionalTextSchema,
    phone: nullableOptionalTextSchema,
    email: nullableOptionalTextSchema,
    dateOfBirth: nullableOptionalTextSchema,
    idDocumentType: nullableOptionalTextSchema,
    idDocumentSeries: nullableOptionalTextSchema,
    idDocumentNumber: nullableOptionalTextSchema,
    idDocumentIssuer: nullableOptionalTextSchema,
    idDocumentIssueDate: nullableOptionalTextSchema,
});

export const otkReceiverSnapshotSchema = z.object({
    fullName: z.string(),
    dateOfBirth: nullableOptionalTextSchema,
    powerOfAttorneySeries: nullableOptionalTextSchema,
    powerOfAttorneyNumber: nullableOptionalTextSchema,
    powerOfAttorneyDate: z.iso.date().nullable().optional(),
    idDocumentType: nullableOptionalTextSchema,
    idDocumentSeries: nullableOptionalTextSchema,
    idDocumentNumber: nullableOptionalTextSchema,
    idDocumentIssuer: nullableOptionalTextSchema,
    idDocumentIssueDate: nullableOptionalTextSchema,
});

export const otkTestResultsSchema = z.object({
    totalSpecificBrakingForce: z.number(),
    brakeForceImbalanceRatio: nullableOptionalNumberSchema,
    brakeResponseTime: nullableOptionalNumberSchema,
    parkingBrakeSpecificForce: nullableOptionalNumberSchema,
    brakeForceOvality: nullableOptionalNumberSchema,
    engineType: otkEmissionTestTypeSchema,
    coAtIdleRpm: nullableOptionalNumberSchema,
    cnhmAtIdleRpm: nullableOptionalNumberSchema,
    coAtElevatedRpm: nullableOptionalNumberSchema,
    cnhmAtElevatedRpm: nullableOptionalNumberSchema,
    lambda: nullableOptionalNumberSchema,
    smokeOpacityK: nullableOptionalNumberSchema,
    windshieldMarked: z.boolean().optional(),
    windshield: nullableOptionalNumberSchema,
    leftSideMarked: z.boolean().optional(),
    leftSide: nullableOptionalNumberSchema,
    rightSideMarked: z.boolean().optional(),
    rightSide: nullableOptionalNumberSchema,
    rearWindowMarked: z.boolean().optional(),
    rearWindow: nullableOptionalNumberSchema,
    videoUrl: nullableOptionalTextSchema,
});

export const otkNonconformitySchema = z.object({
    form: otkNonconformityFormSchema,
    nonconformityCodeId: nullableOptionalTextSchema,
    defectType: otkDefectSeveritySchema.nullable().optional(),
    description: nullableOptionalTextSchema,
});

export const otkReconstructionSchema = z.object({
    approved: z.boolean(),
    documentName: nullableOptionalTextSchema,
    documentNumber: nullableOptionalTextSchema,
    documentDate: z.iso.date().nullable().optional(),
    description: nullableOptionalTextSchema,
});

export const uploadClientPayloadSchema = z.object({
    property: z.enum(photoCategories),
    fileName: z.string(),
    size: z.number(),
    sha256: z.string().regex(/^[a-f0-9]{64}$/),
});
export type UploadClientPayload = z.infer<typeof uploadClientPayloadSchema>;

export const otkDocumentPhotoSchema = uploadClientPayloadSchema.extend({
    pathname: z.string(),
    contentType: z.string(),
    etag: z.string(),
    sha256: uploadClientPayloadSchema.shape.sha256.optional(),
    url: z.string().optional(),
});
/** Metadata for one photo; the binary is carried separately as multipart data. */
export type OtkDocumentPhoto = z.infer<typeof otkDocumentPhotoSchema>;

export const otkDocumentPhotosSchema = z.object({
    files: z.array(otkDocumentPhotoSchema).optional(),
});

const signedOtkDocumentPhotoSchema = z.object({
    property: otkDocumentPhotoSchema.shape.property,
    sha256: otkDocumentPhotoSchema.shape.sha256.unwrap(),
}).strict();

export const otkDocumentFilesResponseSchema = z.object({
    files: z.array(otkDocumentPhotoSchema.required({url: true})),
});
export type OtkDocumentFilesResponse = z.infer<typeof otkDocumentFilesResponseSchema>;

export const otkDocumentSubmitSchema = otkDocumentPhotosSchema.extend({
    type: documentTypeSchema,
    result: otkDocumentResultSchema,
    blankSeries: nullableOptionalIdentifierSchema,
    blankNumber: nullableOptionalIdentifierSchema,
    electronicDocumentSeries: z.string().trim().min(1),
    electronicDocumentNumber: z.string().trim().min(1),
    punktId: z.string().trim().min(1),
    vehicle: otkVehicleSnapshotSchema,
    owner: otkOwnerSnapshotSchema,
    receiverIsOwner: z.boolean(),
    receiver: otkReceiverSnapshotSchema.nullable().optional(),
    isTaxi: z.boolean(),
    transportsDangerousGoods: z.boolean().optional(),
    testResults: otkTestResultsSchema,
    nonconformities: z.array(otkNonconformitySchema).optional(),
    reconstructions: z.array(otkReconstructionSchema).optional(),
    notes: nullableOptionalTextSchema,
});
/** Complete EDRTZ `OtkDocumentSubmit` body; files are sent separately. */
export type OtkDocumentSubmit = z.infer<typeof otkDocumentSubmitSchema>;

export const requiredPhotoCategories = ["DASHBOARD", "RIGHT_SIDE", "LEFT_SIDE", "FRONT", "REAR"] as const;

/** Derives the optional registry field from the single stored source of truth. */
export function deriveDangerousGoodsSubmitField(
    purposes: readonly string[] | null | undefined,
): Partial<Pick<OtkDocumentSubmit, "transportsDangerousGoods">> {
    return purposes?.includes(dangerousGoodsVehiclePurpose)
        ? {transportsDangerousGoods: true}
        : {};
}

const otkVehicleSnapshotWithNullDefaultsSchema = otkVehicleSnapshotSchema.extend({
    registrationCertificate: otkVehicleSnapshotSchema.shape.registrationCertificate.default(null),
    vin: otkVehicleSnapshotSchema.shape.vin.default(null),
    bodyNumber: otkVehicleSnapshotSchema.shape.bodyNumber.default(null),
    chassisNumber: otkVehicleSnapshotSchema.shape.chassisNumber.default(null),
    type: otkVehicleSnapshotSchema.shape.type.default(null),
    registrationDate: otkVehicleSnapshotSchema.shape.registrationDate.default(null),
    emissionLevel: otkVehicleSnapshotSchema.shape.emissionLevel.default(null),
    previousOdometer: otkVehicleSnapshotSchema.shape.previousOdometer.default(null),
    purposes: otkVehicleSnapshotSchema.shape.purposes.default(null),
});

const otkOwnerSnapshotWithNullDefaultsSchema = otkOwnerSnapshotSchema.extend({
    fullName: otkOwnerSnapshotSchema.shape.fullName.default(null),
    taxId: otkOwnerSnapshotSchema.shape.taxId.default(null),
    organizationName: otkOwnerSnapshotSchema.shape.organizationName.default(null),
    edrpou: otkOwnerSnapshotSchema.shape.edrpou.default(null),
    postalAddress: otkOwnerSnapshotSchema.shape.postalAddress.default(null),
    phone: otkOwnerSnapshotSchema.shape.phone.default(null),
    email: otkOwnerSnapshotSchema.shape.email.default(null),
    dateOfBirth: otkOwnerSnapshotSchema.shape.dateOfBirth.default(null),
    idDocumentType: otkOwnerSnapshotSchema.shape.idDocumentType.default(null),
    idDocumentSeries: otkOwnerSnapshotSchema.shape.idDocumentSeries.default(null),
    idDocumentNumber: otkOwnerSnapshotSchema.shape.idDocumentNumber.default(null),
    idDocumentIssuer: otkOwnerSnapshotSchema.shape.idDocumentIssuer.default(null),
    idDocumentIssueDate: otkOwnerSnapshotSchema.shape.idDocumentIssueDate.default(null),
});

const otkReceiverSnapshotWithNullDefaultsSchema = otkReceiverSnapshotSchema.extend({
    dateOfBirth: otkReceiverSnapshotSchema.shape.dateOfBirth.default(null),
    powerOfAttorneySeries: otkReceiverSnapshotSchema.shape.powerOfAttorneySeries.default(null),
    powerOfAttorneyNumber: otkReceiverSnapshotSchema.shape.powerOfAttorneyNumber.default(null),
    powerOfAttorneyDate: otkReceiverSnapshotSchema.shape.powerOfAttorneyDate.default(null),
    idDocumentType: otkReceiverSnapshotSchema.shape.idDocumentType.default(null),
    idDocumentSeries: otkReceiverSnapshotSchema.shape.idDocumentSeries.default(null),
    idDocumentNumber: otkReceiverSnapshotSchema.shape.idDocumentNumber.default(null),
    idDocumentIssuer: otkReceiverSnapshotSchema.shape.idDocumentIssuer.default(null),
    idDocumentIssueDate: otkReceiverSnapshotSchema.shape.idDocumentIssueDate.default(null),
});

const otkTestResultsWithNullDefaultsSchema = otkTestResultsSchema.extend({
    brakeForceImbalanceRatio: otkTestResultsSchema.shape.brakeForceImbalanceRatio.default(null),
    brakeResponseTime: otkTestResultsSchema.shape.brakeResponseTime.default(null),
    parkingBrakeSpecificForce: otkTestResultsSchema.shape.parkingBrakeSpecificForce.default(null),
    brakeForceOvality: otkTestResultsSchema.shape.brakeForceOvality.default(null),
    coAtIdleRpm: otkTestResultsSchema.shape.coAtIdleRpm.default(null),
    cnhmAtIdleRpm: otkTestResultsSchema.shape.cnhmAtIdleRpm.default(null),
    coAtElevatedRpm: otkTestResultsSchema.shape.coAtElevatedRpm.default(null),
    cnhmAtElevatedRpm: otkTestResultsSchema.shape.cnhmAtElevatedRpm.default(null),
    lambda: otkTestResultsSchema.shape.lambda.default(null),
    smokeOpacityK: otkTestResultsSchema.shape.smokeOpacityK.default(null),
    windshieldMarked: otkTestResultsSchema.shape.windshieldMarked
        .default(defaultOtkDraftValues.testResults.windshieldMarked),
    windshield: otkTestResultsSchema.shape.windshield.default(null),
    leftSideMarked: otkTestResultsSchema.shape.leftSideMarked
        .default(defaultOtkDraftValues.testResults.leftSideMarked),
    leftSide: otkTestResultsSchema.shape.leftSide.default(null),
    rightSideMarked: otkTestResultsSchema.shape.rightSideMarked
        .default(defaultOtkDraftValues.testResults.rightSideMarked),
    rightSide: otkTestResultsSchema.shape.rightSide.default(null),
    rearWindowMarked: otkTestResultsSchema.shape.rearWindowMarked
        .default(defaultOtkDraftValues.testResults.rearWindowMarked),
    rearWindow: otkTestResultsSchema.shape.rearWindow.default(null),
    videoUrl: otkTestResultsSchema.shape.videoUrl.default(null),
});

const otkNonconformityWithNullDefaultsSchema = otkNonconformitySchema.extend({
    nonconformityCodeId: otkNonconformitySchema.shape.nonconformityCodeId.default(null),
    defectType: otkNonconformitySchema.shape.defectType.default(null),
    description: otkNonconformitySchema.shape.description.default(null),
});

const otkReconstructionWithNullDefaultsSchema = otkReconstructionSchema.extend({
    documentName: otkReconstructionSchema.shape.documentName.default(null),
    documentNumber: otkReconstructionSchema.shape.documentNumber.default(null),
    documentDate: otkReconstructionSchema.shape.documentDate.default(null),
    description: otkReconstructionSchema.shape.description.default(null),
});

/** Canonical submit payload; absent nullable draft fields are emitted as explicit JSON `null`. */
export const otkDocumentSubmitWithNullDefaultsSchema = otkDocumentSubmitSchema.extend({
    blankSeries: otkDocumentSubmitSchema.shape.blankSeries.default(null),
    blankNumber: otkDocumentSubmitSchema.shape.blankNumber.default(null),
    vehicle: otkVehicleSnapshotWithNullDefaultsSchema,
    owner: otkOwnerSnapshotWithNullDefaultsSchema,
    receiver: otkReceiverSnapshotWithNullDefaultsSchema.nullable().default(null),
    testResults: otkTestResultsWithNullDefaultsSchema,
    nonconformities: z.array(otkNonconformityWithNullDefaultsSchema).optional(),
    reconstructions: z.array(otkReconstructionWithNullDefaultsSchema).optional(),
    notes: otkDocumentSubmitSchema.shape.notes.default(null),
});

/** Complete client-selected registry payload signed verbatim before submission. */
export const signedOtkDocumentPayloadSchema = otkDocumentSubmitSchema
    .extend({files: z.array(signedOtkDocumentPhotoSchema)})
    .strict()
    .superRefine((document, ctx) => {
        const hasBlankSeries = Boolean(document.blankSeries);
        const hasBlankNumber = Boolean(document.blankNumber);
        if (hasBlankSeries !== hasBlankNumber) {
            ctx.addIssue({
                code: "custom",
                path: [hasBlankSeries ? "blankNumber" : "blankSeries"],
                message: "Physical blank series and number must be provided together",
            });
        }
        if (document.type === "NONCONFORMITY_ACT" && (hasBlankSeries || hasBlankNumber)) {
            ctx.addIssue({
                code: "custom",
                path: ["blankSeries"],
                message: "A nonconformity act cannot use a physical protocol blank",
            });
        }
        if ((document.type === "PROTOCOL") !== (document.result === "POSITIVE")) {
            ctx.addIssue({
                code: "custom",
                path: ["result"],
                message: "Document type and result do not match",
            });
        }
        if (document.receiverIsOwner && document.receiver != null) {
            ctx.addIssue({code: "custom", path: ["receiver"], message: "Receiver must be omitted for the owner"});
        } else if (!document.receiverIsOwner && document.receiver == null) {
            ctx.addIssue({code: "custom", path: ["receiver"], message: "Receiver is required"});
        }

        const files = document.files ?? [];
        if (files.length > 15) {
            ctx.addIssue({code: "custom", path: ["files"], message: "No more than 15 photos are allowed"});
        }
        const categories = files.map(({property}) => property);
        const required = document.vehicle.category === "M2" || document.vehicle.category === "M3"
            ? [...requiredPhotoCategories, "INTERIOR_M2_M3" as const]
            : requiredPhotoCategories;
        for (const category of required) {
            if (!categories.includes(category)) {
                ctx.addIssue({code: "custom", path: ["files"], message: `Required photo is missing: ${category}`});
            }
        }
        for (const category of photoCategories) {
            if (category !== "ADDITIONAL" && categories.filter((value) => value === category).length > 1) {
                ctx.addIssue({code: "custom", path: ["files"], message: `Duplicate photo category: ${category}`});
            }
        }
    });
export type SignedOtkDocumentPayload = z.infer<typeof signedOtkDocumentPayloadSchema>;

export const otkDocumentSchema = otkDocumentSubmitSchema.extend({
    id: otkObjectIdCodec,
    subjectId: otkObjectIdCodec,
    documentNumber: z.string(),
    documentSequence: z.number(),
    status: documentStatusSchema,
    blankId: nullableOptionalTextSchema,
    electronicDocumentId: nullableOptionalTextSchema,
    vehicleId: nullableOptionalTextSchema,
    ownerId: nullableOptionalTextSchema,
    receiverId: nullableOptionalTextSchema,
    responsiblePersonId: nullableOptionalTextSchema,
    punktManagerSnapshot: nullableOptionalTextSchema,
    subjectNameSnapshot: nullableOptionalTextSchema,
    punktAddressSnapshot: nullableOptionalTextSchema,
    createdAt: otkTimestampCodec,
    createdBy: z.string(),
    updatedAt: otkTimestampCodec,
    updatedBy: z.string(),
    version: z.number(),
    hasNonconformities: z.boolean().optional(),
    hasReconstructions: z.boolean().optional(),
    signedAt: otkTimestampCodec.nullable().optional(),
    registryAcceptedAt: otkTimestampCodec.nullable().optional(),
    submission: otkDocumentSubmissionSchema.optional(),
    deliveredAt: otkTimestampCodec.nullable().optional(),
    retestDate: z.iso.date().nullable().optional(),
    rejectionReason: nullableOptionalTextSchema,
});
export const otkDocumentDetailSchema = otkDocumentSchema
    .partial()
    .extend({
        vehicle: otkVehicleSnapshotSchema.partial().optional(),
        owner: otkOwnerSnapshotSchema.partial().optional(),
        receiver: otkReceiverSnapshotSchema.partial().nullable().optional(),
        testResults: otkTestResultsSchema.partial().optional(),
    })
    .required({
        id: true,
        subjectId: true,
        documentNumber: true,
        documentSequence: true,
        status: true,
        type: true,
        createdAt: true,
        createdBy: true,
        updatedAt: true,
        updatedBy: true,
        version: true,
    });
/** UI contract whose draft body fields remain optional until signing validation. */
export type OtkDocumentDetail = z.infer<typeof otkDocumentDetailSchema>;

export const otkDocumentViewSchema = z.object({
    id: otkObjectIdCodec,
    number: z.string(),
    plateNumber: z.string().optional(),
    vin: nullableOptionalTextSchema,
    vehicleBrand: z.string().optional(),
    vehicleModel: z.string().optional(),
    type: documentTypeSchema,
    status: documentStatusSchema,
    createdAt: otkTimestampCodec,
    signedAt: otkTimestampCodec.nullable().optional(),
    registryAcceptedAt: otkTimestampCodec.nullable().optional(),
    submission: otkDocumentSubmissionSchema.optional(),
    punktId: z.string().optional(),
    version: z.number(),
});
/** Minimal row returned by `GET /api/otk/documents`. */
export type OtkDocumentView = z.infer<typeof otkDocumentViewSchema>;

export const documentsPageResultSchema = z.object({
    data: z.array(otkDocumentViewSchema),
    nextPage: z.string().nullable(),
});
export type DocumentsPageResult = z.infer<typeof documentsPageResultSchema>;

export const otkDocumentSearchResultSchema = otkDocumentViewSchema.extend({
    registrationCertificate: nullableOptionalTextSchema,
    vehicleType: nullableOptionalTextSchema,
    ownerType: otkOwnerTypeSchema.optional(),
    ownerName: nullableOptionalTextSchema,
    ownerTaxId: nullableOptionalTextSchema,
});
/** Row returned by advanced search, including every searchable document value. */
export type OtkDocumentSearchResult = z.infer<typeof otkDocumentSearchResultSchema>;

export const documentsSearchPageResultSchema = z.object({
    data: z.array(otkDocumentSearchResultSchema),
    nextPage: z.string().nullable(),
});

export type DocumentsSearchPageResult = z.infer<typeof documentsSearchPageResultSchema>;

export const otkDocumentsSearchInputSchema = z.object({
    punktIds: z.array(z.string().trim().min(1)).min(1).max(20).optional(),
    documentSequence: z.number().int().min(1).max(999_999).optional(),
    signedFrom: z.iso.date().optional(),
    signedTo: z.iso.date().optional(),
    types: z.array(documentTypeSchema).min(1).max(2).optional(),
    statuses: z.array(documentStatusSchema).min(1).max(DocumentStatuses.length).optional(),
    vehicle: z.object({
        plate: z.string().trim().min(4).max(8)
            .regex(/^[\p{L}\p{N}]+$/u)
            .optional(),
        vin: z.string().trim().min(4).max(17)
            .regex(/^[A-HJ-NPR-Z0-9]+$/i)
            .optional(),
        registrationCertificate: z.string().trim().min(4).max(100)
            .regex(/^[\p{L}\p{N}]+$/u)
            .optional(),
        brand: z.string().trim().min(1).max(50).optional(),
        model: z.string().trim().min(1).max(50).optional(),
        type: z.string().trim().min(1).max(50).optional(),
    }).optional(),
    owner: z.object({
        type: otkOwnerTypeSchema.optional(),
        name: z.string().trim().min(2).max(500).optional(),
        taxId: z.string().trim()
            .regex(/^\d{4,10}$/, "Must contain between 4 and 10 digits")
            .optional(),
    }).optional(),
    pageSize: z.number().int().min(5).max(50).default(20),
    cursor: z.string().min(1).max(4096).optional(),
    sort: SortSchema.default("DESC"),
}).superRefine(({signedFrom, signedTo}, ctx) => {
    if (signedFrom && signedTo && signedFrom > signedTo) {
        ctx.addIssue({
            code: "custom",
            path: ["signedTo"],
            message: "signedTo must be on or after signedFrom",
        });
    }
});

export type OtkDocumentsSearchInput = z.infer<typeof otkDocumentsSearchInputSchema>;

export const createdDraftDocumentSchema = otkDocumentDetailSchema.pick({
    id: true,
    createdBy: true,
    documentNumber: true,
    documentSequence: true,
    status: true,
    subjectId: true,
    type: true,
    createdAt: true,
    updatedAt: true,
    updatedBy: true,
    version: true,
}).extend({status: z.literal("DRAFT")});
export type CreatedDraftDocument = z.infer<typeof createdDraftDocumentSchema>;

/**
 * Backend-editable draft field schemas (documents-service.ts's
 * `PATCH /otk/documents/:id`), keyed by the same dot-path the patch
 * operations use. Shared so otk-ui can validate a field locally with the
 * exact rules the backend enforces, instead of hand-duplicating regexes
 * (VIN, plate, …) that could drift out of sync between the two packages.
 *
 * `validatePatchOperations` (documents-service.ts) only calls
 * `schema.safeParse(value)` for a `"set"` operation — an `"unset"` skips
 * validation entirely, because emptying a field is always a valid unset
 * regardless of what its schema requires when a value *is* provided
 * (`vehicle.brand` is `min(1)`, but clearing it isn't invalid, it's a
 * request to unset `vehicle.brand`). Callers that build a validation error
 * message from these schemas must preserve that same split — validate only
 * the value of a `"set"`, never treat an empty/unset field as an error.
 */
export const editableDraftFields: Readonly<Record<string, z.ZodType>> = {
    "vehicle.plateNumber": z.string().trim()
        .transform((value) => value.toUpperCase())
        .pipe(z.string().min(1, "Обов'язкове поле").max(8, "Не більше 8 символів")
            .regex(/^[\p{L}\p{N}]+$/u, "Лише літери та цифри")),
    "vehicle.registrationCertificate": z.string().trim()
        .transform((value) => value.toUpperCase())
        .pipe(z.string().min(1, "Обов'язкове поле").max(100, "Не більше 100 символів")
            .regex(/^[\p{L}\p{N}]+$/u, "Лише літери та цифри"))
        .nullable(),
    // EDRTZ's vehicle model defines a final VIN as exactly 17 characters and
    // uses this alphabet to exclude I/O/Q: docs/edrtz/TZ_119.md §3.2.1.
    "vehicle.vin": z.string().trim()
        .transform((value) => value.toUpperCase())
        .pipe(z.string().regex(/^[A-HJ-NPR-Z0-9]{17}$/, "VIN має містити рівно 17 символів (без I, O, Q)"))
        .nullable(),
    "vehicle.bodyNumber": z.string().trim().nullable(),
    "vehicle.chassisNumber": z.string().trim().nullable(),
    "vehicle.brand": z.string().trim().min(1, "Обов'язкове поле"),
    "vehicle.model": z.string().trim().min(1, "Обов'язкове поле"),
    "vehicle.category": z.string().trim().min(1, "Обов'язкове поле"),
    "vehicle.type": z.string().trim().min(1, "Обов'язкове поле"),
    "vehicle.manufactureYear": z.number().int().min(1900, "Не раніше 1900").max(3000, "Не пізніше 3000"),
    "vehicle.registrationDate": z.iso.date().nullable(),
    "vehicle.emissionLevel": z.string().trim().nullable(),
    "vehicle.currentOdometer": z.number().nonnegative("Не може бути від'ємним"),
    "vehicle.previousOdometer": z.number().nonnegative("Не може бути від'ємним"),
    "vehicle.purposes": z.array(z.string().trim().min(1)).nullable(),
    "owner.type": otkOwnerTypeSchema,
    "owner.fullName": z.string().trim().nullable(),
    "owner.dateOfBirth": z.iso.date().nullable(),
    "owner.taxId": z.string().trim().length(10, "ІПН / РНОКПП має містити 10 символів").nullable(),
    "owner.organizationName": z.string().trim().nullable(),
    "owner.edrpou": z.string().trim().nullable(),
    "owner.phone": z.string().trim().nullable(),
    "owner.email": z.string().trim().nullable(),
    "owner.postalAddress": z.string().trim().nullable(),
    "owner.idDocumentType": z.string().trim().nullable(),
    "owner.idDocumentSeries": z.string().trim().nullable(),
    "owner.idDocumentNumber": z.string().trim().nullable(),
    "owner.idDocumentIssuer": z.string().trim().nullable(),
    "owner.idDocumentIssueDate": z.iso.date().nullable(),
    receiverIsOwner: z.boolean(),
    "receiver.fullName": z.string().trim().min(1, "Обов'язкове поле"),
    "receiver.dateOfBirth": z.iso.date().nullable(),
    "receiver.powerOfAttorneySeries": z.string().trim().nullable(),
    "receiver.powerOfAttorneyNumber": z.string().trim().nullable(),
    "receiver.powerOfAttorneyDate": z.iso.date().nullable(),
    "receiver.idDocumentType": z.string().trim().nullable(),
    "receiver.idDocumentSeries": z.string().trim().nullable(),
    "receiver.idDocumentNumber": z.string().trim().nullable(),
    "receiver.idDocumentIssuer": z.string().trim().nullable(),
    "receiver.idDocumentIssueDate": z.iso.date().nullable(),
    isTaxi: z.boolean(),
    "testResults.totalSpecificBrakingForce": z.number(),
    "testResults.brakeForceImbalanceRatio": z.number(),
    "testResults.brakeResponseTime": z.number(),
    "testResults.parkingBrakeSpecificForce": z.number(),
    "testResults.brakeForceOvality": z.number().nullable(),
    "testResults.engineType": otkEmissionTestTypeSchema,
    "testResults.coAtIdleRpm": z.number().nullable(),
    "testResults.cnhmAtIdleRpm": z.number().nullable(),
    "testResults.coAtElevatedRpm": z.number().nullable(),
    "testResults.cnhmAtElevatedRpm": z.number().nullable(),
    "testResults.lambda": z.number().nullable(),
    "testResults.smokeOpacityK": z.number().nullable(),
    "testResults.windshieldMarked": z.boolean(),
    "testResults.windshield": z.number().nullable(),
    "testResults.leftSideMarked": z.boolean(),
    "testResults.leftSide": z.number().nullable(),
    "testResults.rightSideMarked": z.boolean(),
    "testResults.rightSide": z.number().nullable(),
    "testResults.rearWindowMarked": z.boolean(),
    "testResults.rearWindow": z.number().nullable(),
    "testResults.videoUrl": z.url("Некоректне посилання").nullable(),
    notes: z.string().max(2000, "Не більше 2000 символів").nullable(),
};

/** `undefined` for a valid (or unknown-path) value, else the schema's own message. */
export function editableDraftFieldError(path: string, value: unknown): string | undefined {
    const schema = editableDraftFields[path];
    if (!schema) return undefined;
    const result = schema.safeParse(value);
    return result.success ? undefined : result.error.issues[0]?.message;
}
